import React, { useMemo } from 'react';
import { Layout } from 'fronton-react';
import { useLocation, useParams } from '@okapi/router-react';
import {
  ConsultationSection,
  HeaderSection,
  OtherSection,
  RootLayout,
  SpecialOffer,
  StepsSection,
  UtpCardSection,
} from 'common/components';
import { MODEL_JSON_STORE_NAME, selectRegionId } from 'modules/model-json';
import { State } from 'store/types';
import { useStoreon } from 'storeon/react';
import { useIsInViewport, useScrollToElement } from 'common/hooks';
import {
  METAL_ROOF_UTP_CARD_LIST_WITH_MOUNTING,
  MetalRoofCharacteristics,
  MetalRoofDesignPanel,
  MetalRoofGallerySection,
  MetalRoofInfo,
  MetalRoofOtherTypes,
  MetalRoofPartners,
  MetalRoofTypesSection,
} from './components';
import {
  METAL_ROOF_CONSULTATION_TYPE,
  METAL_ROOF_ELEMENT_BLOCK_MAP,
  METAL_ROOF_HEADER_MAP,
  METAL_ROOF_LANDING_NAME,
  METAL_ROOF_STEP_LIST_WITH_MOUNTING,
  METAL_ROOF_TYPE_NAME_MAP,
  REGION_LIST_WITHOUT_SEAM_ROOFING,
} from './metal-roof-constants';

type MetalRoofProps = {
  ssrRoofType?: string;
};

/**
 * Вью лендинга Металлическая кровля под заказ
 */
export const MetalRoof: React.FC<MetalRoofProps> = ({ ssrRoofType }) => {
  const { elRef, onScrollToElement } = useScrollToElement();
  const params = useParams();
  const [path] = useLocation();
  const roofType = ssrRoofType || params.roofType;
  const { modelJson } = useStoreon<State>(MODEL_JSON_STORE_NAME);
  const regionCode = useMemo(() => selectRegionId(modelJson), [modelJson]);
  const withSeamRoofing = !REGION_LIST_WITHOUT_SEAM_ROOFING.includes(regionCode);

  const isConsultationFormVisible = useIsInViewport(elRef, { threshold: 0 });
  const pageName = roofType || 'main-page';
  const headerConfig = METAL_ROOF_HEADER_MAP[pageName];
  const elementBlock = METAL_ROOF_ELEMENT_BLOCK_MAP[pageName];

  return (
    <RootLayout currentPage={path}>
      <HeaderSection {...headerConfig} mediaType="image" />

      <MetalRoofDesignPanel
        elementBlock={elementBlock}
        isConsultationFormVisible={isConsultationFormVisible}
        onConsultationSignupBtnClick={onScrollToElement}
      />

      <Layout>
        <SpecialOffer onModalBtnClick={onScrollToElement} elementBlock={elementBlock} />
        <UtpCardSection
          elementBlock={elementBlock}
          elementText="Почему нам доверяют"
          renderModalContent={({ modal: { content: ModalContent } = {} }, onCloseModal) => (
            ModalContent && <ModalContent
              elementBlock={elementBlock}
              onClick={() => {
                onCloseModal();
                onScrollToElement();
              }}
            />
          )}
          title="Почему нам доверяют"
          utpCardList={METAL_ROOF_UTP_CARD_LIST_WITH_MOUNTING}
        />
        <MetalRoofInfo />

        {roofType && <MetalRoofCharacteristics elementBlock={elementBlock} roofType={roofType} />}
      </Layout>

      {!roofType && <MetalRoofTypesSection elementBlock={elementBlock} withSeamRoofing={withSeamRoofing} />}

      <Layout>
        {roofType !== 'ondulin' && (
          <MetalRoofGallerySection
            elementBlock={elementBlock}
            onConsultationSignupBtnClick={onScrollToElement}
            roofType={roofType}
            withSeamRoofing={withSeamRoofing}
          />
        )}

        {!roofType && <MetalRoofPartners />}

        <StepsSection sectionTitle="Как заказать кровлю" steps={METAL_ROOF_STEP_LIST_WITH_MOUNTING} />
        <ConsultationSection
          isCommentVisible
          arrowBottomPosition={272}
          hardcodeComment={METAL_ROOF_TYPE_NAME_MAP[pageName]}
          consultationType={METAL_ROOF_CONSULTATION_TYPE}
          elementBlock={elementBlock}
          elementText="Заявка на консультацию и расчёт"
          sectionRef={(el: HTMLElement | null) => {
            elRef.current = el;
          }}
          sectionTitle="Отправьте заявку на&nbsp;установку"
          sectionText="Перезвоним в&nbsp;ближайшее время, детально обсудим проект и&nbsp;ответим на&nbsp;все вопросы"
        />

        {roofType && (
          <MetalRoofOtherTypes
            elementBlock={elementBlock}
            roofType={roofType}
            withSeamRoofing={withSeamRoofing}
          />
        )}
      </Layout>

      <OtherSection
        elementBlock={`${elementBlock} / other`}
        landingName={METAL_ROOF_LANDING_NAME}
        promotionName={`${elementBlock}_bottom`}
      />
    </RootLayout>
  );
};
